/**
 * Admin › Events — moderation queue + status snapshot + AI backfill + manual discover trigger.
 */
import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, X, Archive, Trash2, Sparkles, ExternalLink, Compass } from "lucide-react";
import AdminEventFeeds from "./AdminEventFeeds";

export default function AdminEvents() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, s] = await Promise.all([
        http.get("/admin/events", { params: { status } }),
        http.get("/admin/events/status"),
      ]);
      setItems(Array.isArray(i.data) ? i.data : []);
      setSnapshot(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => { try { await http.post(`/admin/events/${id}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error("Approve failed"); } };
  const reject = async (id) => { const reason = window.prompt("Reject reason (optional):"); try { await http.post(`/admin/events/${id}/reject`, null, { params: reason ? { reason } : {} }); toast.success("Rejected"); load(); } catch (e) { toast.error("Reject failed"); } };
  const archive = async (id) => { if (!window.confirm("Archive this event?")) return; try { await http.post(`/admin/events/${id}/archive`); toast.success("Archived"); load(); } catch (e) { toast.error("Archive failed"); } };
  const del = async (id) => { if (!window.confirm("Delete permanently?")) return; try { await http.delete(`/admin/events/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Delete failed"); } };
  const bulkBackfill = async () => { try { await http.post("/admin/events/ai-summaries/backfill"); toast.success("AI backfill queued"); } catch (e) { toast.error("Failed"); } };
  const discover = async () => {
    const city = window.prompt("Discover events for which city? (Phase 2 — will use Meetup + Eventbrite if configured)");
    if (!city) return;
    try {
      const { data } = await http.post("/admin/events/discover", null, { params: { city } });
      toast.success(`Meetup: ${data.meetup.enabled ? "queued" : "disabled"} · Eventbrite: ${data.eventbrite.enabled ? "queued" : "disabled"}`);
    } catch (e) { toast.error("Discovery failed"); }
  };

  const counts = snapshot?.counts || {};
  const ai = snapshot?.ai_summaries || {};
  const src = snapshot?.sources || {};

  const filters = [
    { v: "pending", label: `Pending (${counts.pending ?? "…"})` },
    { v: "approved", label: `Approved (${counts.approved ?? "…"})` },
    { v: "rejected", label: `Rejected (${counts.rejected ?? "…"})` },
    { v: "archived", label: `Archived (${counts.archived ?? "…"})` },
    { v: "all", label: "All" },
  ];

  return (
    <div data-testid="admin-events-panel">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline mb-2">EVENTS · MODERATION</div>
          <h2 className="font-display text-3xl">Community events queue.</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={bulkBackfill} data-testid="admin-events-ai-backfill" className="wofy-btn-ghost text-sm">
            <Sparkles className="w-4 h-4" /> Backfill AI summaries ({ai.missing ?? 0})
          </button>
          <button onClick={discover} data-testid="admin-events-discover" className="wofy-btn-primary text-sm">
            <Compass className="w-4 h-4" /> Discover events
          </button>
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-4">
        <div className="wofy-card">
          <div className="overline">TOTALS</div>
          <div className="font-display text-2xl mt-1">{(counts.pending ?? 0) + (counts.approved ?? 0)}</div>
          <div className="text-xs opacity-60">{counts.pending ?? 0} pending · {counts.approved ?? 0} live</div>
        </div>
        <div className="wofy-card">
          <div className="overline">AI SUMMARIES</div>
          <div className="font-display text-2xl mt-1">{ai.with_summary ?? 0}</div>
          <div className="text-xs opacity-60">{ai.missing ?? 0} missing</div>
        </div>
        <div className="wofy-card">
          <div className="overline">SOURCE · MEETUP</div>
          <div className={`mt-1 font-mono text-sm ${src.meetup?.enabled ? "text-emerald-600" : "opacity-60"}`}>
            {src.meetup?.enabled ? "READY" : "SCAFFOLD"}
          </div>
          <div className="text-xs opacity-60 mt-1">{src.meetup?.requires}</div>
        </div>
        <div className="wofy-card">
          <div className="overline">SOURCE · EVENTBRITE</div>
          <div className={`mt-1 font-mono text-sm ${src.eventbrite?.enabled ? "text-emerald-600" : "opacity-60"}`}>
            {src.eventbrite?.enabled ? "READY" : "SCAFFOLD"}
          </div>
          <div className="text-xs opacity-60 mt-1">{src.eventbrite?.requires}</div>
        </div>
      </div>

      {/* ICS feed source card — now the real auto-discovery pipeline */}
      <div className="mt-4">
        <div className="wofy-card flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[260px]">
            <div className="overline">SOURCE · ICS / iCAL FEEDS</div>
            <div className="mt-1 font-mono text-sm text-emerald-600">
              {src.ics?.enabled ? "READY · no API key required" : "…"}
            </div>
            <div className="text-xs opacity-70 mt-1">
              {src.ics?.feeds_active ?? 0} active feeds · {src.ics?.events_from_ics ?? 0} events imported
              {(src.ics?.events_pending ?? 0) > 0 ? ` · ${src.ics.events_pending} pending approval` : ""}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-1.5">
        {filters.map((f) => (
          <button
            key={f.v}
            data-testid={`admin-events-filter-${f.v}`}
            onClick={() => setStatus(f.v)}
            className={`px-3 py-1.5 text-xs rounded-full ${status === f.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={status === f.v ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : { border: "1px solid var(--wofy-border)" }}
          >
            {f.label}
          </button>
        ))}
        <button onClick={load} className="wofy-btn-ghost text-xs ml-2"><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>

      {loading && <div className="mt-6 text-sm opacity-60">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="mt-6 wofy-card text-center py-12">
          <div className="font-display text-xl">No {status === "all" ? "" : status} events.</div>
        </div>
      )}
      <div className="mt-6 grid gap-3">
        {items.map((ev) => (
          <div key={ev.id} data-testid={`admin-event-row-${ev.slug || ev.id}`} className="wofy-card flex flex-wrap items-start gap-4">
            <div
              className="w-24 h-24 rounded-xl bg-cover bg-center shrink-0 border"
              style={{ backgroundImage: `url(${ev.cover_image || ""})`, background: "var(--wofy-surface)", borderColor: "var(--wofy-border)" }}
            />
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full"
                  style={{
                    background: ev.status === "pending" ? "#FEF3C7" : ev.status === "approved" ? "#D1FAE5" : ev.status === "archived" ? "#E5E7EB" : "#FEE2E2",
                    color: ev.status === "pending" ? "#92400E" : ev.status === "approved" ? "#065F46" : ev.status === "archived" ? "#374151" : "#991B1B",
                  }}
                >{ev.status}</span>
                <span className="text-[10px] font-mono opacity-60">{ev.category}{ev.subcategory ? ` · ${ev.subcategory}` : ""}</span>
              </div>
              <div className="font-display text-lg">{ev.title}</div>
              <div className="text-xs mt-0.5 opacity-70">
                {[ev.venue, ev.city, ev.country].filter(Boolean).join(" · ")} · {(ev.start_date || "").slice(0,10)}
              </div>
              {ev.short_description && <p className="mt-1 text-sm line-clamp-2">{ev.short_description}</p>}
              <div className="mt-1 text-[11px] font-mono opacity-60">
                by {ev.submitter_name || ev.organizer_name || "system"}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href={`/events/${ev.slug || ev.id}`} target="_blank" rel="noopener noreferrer" className="wofy-btn-ghost text-xs">
                View <ExternalLink className="w-3 h-3" />
              </a>
              {ev.status === "pending" && (
                <button onClick={() => approve(ev.id)} data-testid={`ae-approve-${ev.id}`} className="wofy-btn-primary text-xs">
                  <CheckCircle2 className="w-4 h-4" /> Approve
                </button>
              )}
              {ev.status === "pending" && (
                <button onClick={() => reject(ev.id)} data-testid={`ae-reject-${ev.id}`} className="wofy-btn-ghost text-xs" style={{ color: "#B23A3A" }}>
                  <X className="w-4 h-4" /> Reject
                </button>
              )}
              {ev.status === "approved" && (
                <button onClick={() => archive(ev.id)} className="wofy-btn-ghost text-xs">
                  <Archive className="w-4 h-4" /> Archive
                </button>
              )}
              <button onClick={() => del(ev.id)} className="wofy-btn-ghost text-xs" style={{ color: "#B23A3A" }} aria-label="Delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ICS/iCal auto-discovery feed management */}
      <AdminEventFeeds />
    </div>
  );
}
